import { makeAutoObservable, observableRef } from 'mobx';
import { isString, logger } from '@cutwise/utils';

import B2BAPI from '@/b2b/api/B2BAPI';
import { B2BFilter, B2BFilterResponse } from '@/b2b/types';
import { carbonViewerRepository } from '@/carbonViewer/repository/CarbonViewerRepository';
import { sortRepository } from '@/catalog/repository/SortRepository';
import { viewTemplateRepository } from '@/collection/repository/ViewTemplateRepository';
import ConstantsAPI from '@/common/api/ConstantsAPI';
import useStore from '@/common/hooks/useStore';
import { ConstantsResponse } from '@/common/types/constantsResponse';
import config from '@/config';
import { colorGradeRepository } from '@/customGrading/repositories/ColorGradeRepository';
import { fancyColorGradeRepository } from '@/customGrading/repositories/FancyColorGradeRepository';
import { mediaSubFormatRepository } from '@/media/repository/MediaSubFormatRepository';
import { setupPresetGroupRepository } from '@/media/repository/SetupPresetGroupRepository';
import { setupPresetRepository } from '@/media/repository/SetupPresetRepository';
import { ProductId } from '@/product/entity/Product';

import { dictionaryRepository } from '../repositories/DictionaryRepository';

export default class ConfigurationStore {
  b2bFilters!: Record<string, B2BFilter[]>;

  scaleBaseUri!: string;

  moviePlayerBasePath!: string;

  browserlessBaseUrl!: string;

  wsServerURL!: string;

  fcrfDemoProjectId?: string;

  fcrfDemoProducts?: ProductId[];

  readonly b2bAPI: B2BAPI;

  readonly constantsAPI: ConstantsAPI;

  constructor() {
    makeAutoObservable(this, {
      b2bFilters: observableRef,
      b2bAPI: false,
      constantsAPI: false,
      isFCRFDemoProject: false,
      isFCRFDemoProduct: false,
    });

    this.b2bAPI = new B2BAPI();
    this.constantsAPI = new ConstantsAPI();
  }

  get isConfigurationLoaded() {
    return Boolean(dictionaryRepository.dictionaries) && Boolean(setupPresetRepository.setupPresets);
  }

  fetchB2BFilters = () => {
    if (this.b2bFilters) {
      return Promise.resolve(this.b2bFilters);
    }

    return this.b2bAPI
      .fetchB2BFilters()
      .then((res) => res.data)
      .then(this.processAndApplyB2BList)
      .catch((e) => logger.error(e));
  };

  fetchServerConstants = () => this.constantsAPI
    .fetchAll()
    .then((res) => res.content)
    .then(this.processAndApplyConstants);

  processAndApplyConstants = (serverResponse: ConstantsResponse) => {
    if (!serverResponse) {
      return;
    }

    config.PLAYER_JS_URL = serverResponse.web.player.js;
    config.PLAYER_CSS_URL = serverResponse.web.player.css;

    carbonViewerRepository.initCarbonViewerVersions(
      config.CARBON_VIEWER_CDN_URL || serverResponse.carbonViewerCdnUrl,
      serverResponse.carbonViewerVersions,
    );

    setupPresetGroupRepository.initSorts(serverResponse.productTypes);
    sortRepository.initSorts(serverResponse.dict.sorts, serverResponse.productTypes); // todo make serverResponse immutable
    dictionaryRepository.initDictionaries(serverResponse.dict);
    mediaSubFormatRepository.initMediaSubFormats(serverResponse.dict.subFormats);
    setupPresetRepository.initSetupPresets(serverResponse.dict.setupPresets);
    setupPresetRepository.initSetupPresetOrder(serverResponse.spOrder);
    setupPresetRepository.initSetupPresetsFilteringStructure(serverResponse.dict.setupPresetsFilterStructure);
    setupPresetGroupRepository.initSetupPresetGroups(serverResponse.dict.setupPresetGroups);
    viewTemplateRepository.initViewTemplates(serverResponse.dict.viewTemplates);
    colorGradeRepository.initColorGrades(serverResponse.colorGradeFull);
    fancyColorGradeRepository.initFancyColorGrades(serverResponse.fancyGrade);

    this.scaleBaseUri = serverResponse.web.scaleBaseUri;
    this.wsServerURL = serverResponse.web.wsServerURL;
    this.moviePlayerBasePath = serverResponse.web.moviePlayerBasePath;
    this.browserlessBaseUrl = serverResponse.web.browserlessBaseUrl;
    this.fcrfDemoProjectId = serverResponse.web.fcrfDemoProjectId;
    this.fcrfDemoProducts = serverResponse.web.fcrfDemoProducts;
  };

  processAndApplyB2BList = (serverResponse: B2BFilterResponse) => {
    if (!serverResponse) {
      return;
    }

    const b2bByProductTypes = serverResponse.productTypes;
    const b2bAccounts = serverResponse.b2b;

    // filter every object in "productTypes" by ids from "b2b" array
    (Object.keys(b2bByProductTypes) as (keyof typeof b2bByProductTypes)[]).forEach((productType) => {
      const b2bIds = b2bByProductTypes[productType] as unknown as number[];

      b2bByProductTypes[productType] = b2bIds
        .map((b2bId) => b2bAccounts.find(({ id }) => id === b2bId))
        .filter((type): type is B2BFilter => !!type);
    });

    this.b2bFilters = b2bByProductTypes;
  };

  isFCRFDemoProject = (projectId?: string) => {
    return isString(projectId) && this.fcrfDemoProjectId === projectId;
  };

  isFCRFDemoProduct = (productId: ProductId) => {
    return this.fcrfDemoProducts?.some((id) => id === productId) ?? false;
  };
}

export const useConfigurationStore = () => useStore(ConfigurationStore);

export const configurationStore = new ConfigurationStore();
